/**
 * Palette recolouring for IMAGE elements.
 *
 * Vector colours are remapped as values (utils/paletteRecolor); a bitmap has to be
 * remapped pixel by pixel, and most print artwork in this editor IS a bitmap — an
 * Illustrator/PSD import arrives as image layers, so skipping images meant a palette
 * appeared to do nothing at all on a real template.
 *
 * Same rule as the vector path, so the two agree: each pixel's lightness picks a
 * colour from the palette's light → dark ramp (a gradient map / duotone), alpha is
 * preserved so cut-outs stay cut out, and NEUTRAL WHITES are left alone — white
 * paper and reversed-out logos are white on purpose.
 *
 * The recoloured bitmap is NOT stored in the document. It is derived at render time
 * and cached in memory, keyed by source + palette: the saved template keeps its
 * original artwork (and its R2 media keys), and switching palettes never rewrites —
 * or re-uploads — a single image.
 */
import { parseRgba } from '../panels/propertyControls';
import { WHITE_LUMA, WHITE_CHROMA } from './paletteRecolor';
import { isTemplateMediaUrl, fetchMediaAsDataUrl } from './templateMedia';

/** src|paletteId → Promise<dataUrl | null>. Null means "use the original". */
const cache = new Map();

const key = (src, paletteId) => `${paletteId}|${src}`;

function decode(src, { cors = false } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        // Reading pixels back needs a CORS-clean image; a tainted canvas throws on
        // getImageData, which is why the media fallback below exists.
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${String(src).slice(0, 64)}`));
        img.src = src;
    });
}

async function loadPixelSafeImage(src) {
    if (/^(data:|blob:)/i.test(src)) return decode(src);
    try {
        return await decode(src, { cors: true });
    } catch (err) {
        // Stored template media can always be read through our own API instead,
        // which makes it same-origin — the exporter uses the same escape hatch.
        if (!isTemplateMediaUrl(src)) throw err;
        return decode(await fetchMediaAsDataUrl(src));
    }
}

/**
 * 256-entry lookup table: luminance → ramp colour. Built once per palette so the
 * per-pixel loop is three array reads.
 */
function buildLut(ramp) {
    const stops = ramp.map((c) => parseRgba(c)).filter(Boolean);
    if (!stops.length) return null;
    const r = new Uint8Array(256);
    const g = new Uint8Array(256);
    const b = new Uint8Array(256);
    for (let i = 0; i < 256; i += 1) {
        const l = i / 255;
        // Ramp runs light → dark, matching remapColor()'s indexing exactly.
        const idx = Math.max(0, Math.min(stops.length - 1, Math.round((1 - l) * (stops.length - 1))));
        r[i] = stops[idx].r;
        g[i] = stops[idx].g;
        b[i] = stops[idx].b;
    }
    return { r, g, b };
}

const lutCache = new Map();
function lutFor(ramp) {
    const id = ramp.join(',');
    let lut = lutCache.get(id);
    if (!lut) {
        lut = buildLut(ramp);
        lutCache.set(id, lut);
    }
    return lut;
}

/**
 * Recolour one image onto a ramp. Resolves to a PNG data URL, or null when the
 * pixels can't be read (a cross-origin image with no CORS) — callers then draw the
 * original, which is better than dropping the artwork.
 */
export function recolorImage(src, ramp, paletteId) {
    if (!src || !ramp?.length) return Promise.resolve(null);
    const k = key(src, paletteId || ramp.join(','));
    const hit = cache.get(k);
    if (hit) return hit;

    const task = (async () => {
        try {
            const img = await loadPixelSafeImage(src);
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            if (!w || !h) return null;
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, 0, 0);
            const frame = ctx.getImageData(0, 0, w, h);
            const px = frame.data;
            const lut = lutFor(ramp);
            if (!lut) return null;
            const whiteLuma = WHITE_LUMA * 255;
            const whiteChroma = WHITE_CHROMA * 255;
            for (let i = 0; i < px.length; i += 4) {
                if (px[i + 3] === 0) continue; // fully transparent: nothing to map
                const r = px[i];
                const g = px[i + 1];
                const b = px[i + 2];
                // Rec. 709 luma, the same measure the vector path uses.
                const l = (r * 0.2126 + g * 0.7152 + b * 0.0722) | 0;
                // White pixels stay white: the paper an artwork sits on, and a logo or
                // caption reversed out of a dark panel, are white on purpose. Tinting
                // them to the ramp's lightest step is what turned cards cream and washed
                // reversed logos out. Same test as the vector path (isNeutralWhite).
                if (l >= whiteLuma
                    && (Math.max(r, g, b) - Math.min(r, g, b)) <= whiteChroma) continue;
                px[i] = lut.r[l];
                px[i + 1] = lut.g[l];
                px[i + 2] = lut.b[l];
            }
            ctx.putImageData(frame, 0, 0);
            return canvas.toDataURL('image/png');
        } catch (err) {
            console.warn('[print-editor] Could not recolour image for palette', err?.message || err);
            return null;
        }
    })();

    cache.set(k, task);
    return task;
}

/** Already-computed recolour for this image, if any — lets the editor paint it on the first frame. */
export function peekRecoloredImage(src, paletteId) {
    const task = cache.get(key(src, paletteId));
    return task?.__value || null;
}

/** Remember the resolved value alongside the promise, for peek above. */
export function rememberRecolored(src, paletteId, value) {
    const k = key(src, paletteId);
    const task = cache.get(k);
    if (task) task.__value = value;
}
